// "When did this reader last open this person's correspondence, on this device?" — the persistence half
// of the Outreach screen's NEW dot. Same shape and same refusals as `lib/last-visited.ts`: import-free,
// storage injectable, never throws, never trusts what it reads back.
//
// The mark is per DEVICE, deliberately: the backend carries no per-person seen state (ListContacts has
// none), so the three surfaces each keep their own — the same written-down divergence the summary
// tiles' "new" badge already has. A person opened on the phone still reads as new on the desktop
// until opened there; the "Mark all as read" action clears a whole list at once.

/** The one-word key prefix, named for the product (see orion-web CLAUDE.md on new storage keys). */
export const CONTACT_SEEN_PREFIX = 'symphony-contact-seen:';

export function contactSeenKey(personId: string): string {
  return `${CONTACT_SEEN_PREFIX}${personId}`;
}

/** Parse a stored value into an epoch-ms stamp, or `null` when there isn't a usable one. Pure. */
export function parseSeen(raw: string | null | undefined): number | null {
  if (typeof raw !== 'string' || raw.trim() === '') return null;
  const ms = Number(raw);
  return Number.isFinite(ms) ? ms : null;
}

function safeStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

/** When this device last opened the person, or `null` — including when storage is unreadable. */
export function readSeen(personId: string, storage: Pick<Storage, 'getItem'> | null = safeStorage()): number | null {
  try {
    return parseSeen(storage?.getItem(contactSeenKey(personId)));
  } catch {
    return null;
  }
}

/** Stamp the person as seen now (or at `at`). A storage failure is silently a no-op. */
export function markSeen(personId: string, at: number = Date.now(), storage: Pick<Storage, 'setItem'> | null = safeStorage()): void {
  try {
    storage?.setItem(contactSeenKey(personId), String(at));
  } catch {
    // no storage, private mode, quota — the dot simply persists; nothing else depends on the write
  }
}

/** How long a correspondence stays "new" when this device has NEVER opened the person: the last day. */
export const NEVER_SEEN_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * Whether a person has new correspondence: their last activity is later than this device's mark — or,
 * with no mark at all, within the last day (an old contact never opened here must not light up). Pure.
 */
export function isNewSince(lastActivityAt: string | number | Date | undefined, seenAt: number | null, now: number): boolean {
  if (!lastActivityAt) return false;
  const at = new Date(lastActivityAt).getTime();
  if (!Number.isFinite(at)) return false;
  const since = seenAt ?? now - NEVER_SEEN_WINDOW_MS;
  return at > since;
}
